/**
 * spiceScheduleCopy: the one way a SPICE schedule is put into words, for
 * every surface that shows one (the roster, the SPICE tab, the schedule
 * dialog, the week grid and the record header). Pure; no fetches, no
 * React. The SpiceScheduleSummary component renders these lines.
 *
 * A schedule reads as one line per kind with sessions, SPICE first, then
 * Friendly Visitor, each line that kind's phrases joined with ", ":
 *
 *   - weekly sessions group by time and therapist together, the days
 *     plural and joined with "and": "Mondays and Wednesdays at 10:00 AM
 *     (weekly)"; exactly Monday to Friday reads "Weekdays";
 *   - every-two-weeks sessions read one each, the day singular:
 *     "Friday at 11:00 AM (every two weeks)";
 *   - monthly sessions read one each, naming the week of the month:
 *     "Monthly on the second Tuesday at 10:00 AM";
 *   - a session with a therapist of its own names her before the
 *     frequency: "Wednesdays at 10:00 AM with Bea Lowry (weekly)"; a
 *     session with none reads exactly as above;
 *   - weekly phrases come first, by time, then therapist (none first),
 *     then every-two-weeks phrases by day, then time, then monthly
 *     phrases by day, then week (first to fourth, then last), then time.
 *
 * A duration is never part of the words, and a plan with no sessions
 * reads SPICE_NO_SCHEDULE_TEXT.
 *
 * A plan that is away reads in one pair of phrases everywhere it is
 * shown: "Covered by {name} until {date}" and "Paused until {date}
 * ({reason})" (formatSpiceAway).
 */

import type {
  SpiceHoldReason,
  SpiceScheduleSessionWire,
  SpiceSessionKind,
  SpiceVisitPlanStatus,
  SpiceWeekOfMonth,
} from './spiceVisitPlansApi';
import { effectiveHold } from './spiceHold';

export type SpiceScheduleSession = SpiceScheduleSessionWire;

export const SPICE_NO_SCHEDULE_TEXT = 'No schedule yet';

/** Each kind's label, in the order its line reads. */
export const SPICE_KIND_LABELS: Record<SpiceSessionKind, 'SPICE' | 'Friendly Visitor'> = {
  spice: 'SPICE',
  spice_friendly_visitor: 'Friendly Visitor',
};

const KIND_ORDER: readonly SpiceSessionKind[] = ['spice', 'spice_friendly_visitor'];

/** A frequency as a line of its own reads it ("· Weekly", "· Every two
 *  weeks", "· Monthly"), and the session editor's Frequency options. */
export const SPICE_FREQUENCY_TITLES = {
  weekly: 'Weekly',
  every_two_weeks: 'Every two weeks',
  monthly: 'Monthly',
} as const;

/** The week of the month a monthly session falls in, as the editor's
 *  Which week options read it. */
export const SPICE_WEEK_OF_MONTH_LABELS: Record<SpiceWeekOfMonth, string> = {
  first: 'First',
  second: 'Second',
  third: 'Third',
  fourth: 'Fourth',
  last: 'Last',
};

const WEEK_OF_MONTH_ORDER: Record<SpiceWeekOfMonth, number> = { first: 1, second: 2, third: 3, fourth: 4, last: 5 };

/** ISO weekday (Monday 1 .. Sunday 7) to its name. */
export const SPICE_DAY_NAMES: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

/** ISO weekday (Monday 1 .. Sunday 7) to its short name. */
export const SPICE_DAY_ABBR: Record<number, string> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const HOLD_REASON_WORDS: Record<SpiceHoldReason, string> = {
  vacation: 'Vacation',
  medical: 'Medical',
  other: 'Other',
};

export interface SpiceScheduleLine {
  kind: 'SPICE' | 'Friendly Visitor';
  text: string;
}

/** 'HH:MM' as the en-US 12-hour time Olive shows ("10:00 AM", "2:00 PM"),
 *  worked by arithmetic so every runtime prints the same space. */
export function formatSpiceTime(time: string): string {
  const [hRaw, mRaw] = time.split(':');
  const h = Number(hRaw);
  const minutes = (mRaw ?? '00').slice(0, 2);
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${minutes} ${h < 12 ? 'AM' : 'PM'}`;
}

/** One session's day and time, short: "Tue 2:00 PM". */
export function formatSpiceSlot(slot: { weekday: number; start_time: string }): string {
  return `${SPICE_DAY_ABBR[slot.weekday] ?? ''} ${formatSpiceTime(slot.start_time)}`.trim();
}

/** 'YYYY-MM-DD' as the house short date, "14 Oct". */
export function formatSpiceShortDate(iso: string): string {
  const parts = iso.split('-').map(Number);
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  return `${day} ${MONTHS_SHORT[month - 1] ?? ''}`.trim();
}

function joinWithAnd(words: readonly string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

function pluralDays(weekdays: readonly number[]): string {
  const days = [...new Set(weekdays)].sort((a, b) => a - b);
  if (days.length === 5 && days.every((d, i) => d === i + 1)) return 'Weekdays';
  return joinWithAnd(days.map((d) => `${SPICE_DAY_NAMES[d] ?? ''}s`));
}

/** The session's own therapist's name, when the session names one. */
function sessionTherapistName(s: SpiceScheduleSession): string | null {
  return (s.therapist_roster_id ?? null) !== null && s.therapist_name ? s.therapist_name : null;
}

/** " with {name}" before the frequency, or nothing. */
function withTherapist(name: string | null): string {
  return name === null ? '' : ` with ${name}`;
}

function kindPhrases(sessions: readonly SpiceScheduleSession[]): string[] {
  const weeklyByTimeAndTherapist = new Map<string, { time: string; therapist: string | null; days: number[] }>();
  for (const s of sessions) {
    if (s.frequency !== 'weekly') continue;
    const therapist = sessionTherapistName(s);
    const key = `${s.start_time}|${therapist ?? ''}`;
    const group = weeklyByTimeAndTherapist.get(key) ?? { time: s.start_time, therapist, days: [] };
    group.days.push(s.weekday);
    weeklyByTimeAndTherapist.set(key, group);
  }
  const weekly = [...weeklyByTimeAndTherapist.values()]
    .sort((a, b) => a.time.localeCompare(b.time) || (a.therapist ?? '').localeCompare(b.therapist ?? ''))
    .map((g) => `${pluralDays(g.days)} at ${formatSpiceTime(g.time)}${withTherapist(g.therapist)} (weekly)`);

  const everyTwoWeeks = sessions
    .filter((s) => s.frequency === 'every_two_weeks')
    .slice()
    .sort((a, b) => (a.weekday !== b.weekday ? a.weekday - b.weekday : a.start_time.localeCompare(b.start_time)))
    .map(
      (s) =>
        `${SPICE_DAY_NAMES[s.weekday] ?? ''} at ${formatSpiceTime(s.start_time)}${withTherapist(sessionTherapistName(s))} (every two weeks)`,
    );

  const monthly = sessions
    .filter((s) => s.frequency === 'monthly')
    .slice()
    .sort((a, b) => {
      if (a.weekday !== b.weekday) return a.weekday - b.weekday;
      const wa = a.week_of_month ? WEEK_OF_MONTH_ORDER[a.week_of_month] : 0;
      const wb = b.week_of_month ? WEEK_OF_MONTH_ORDER[b.week_of_month] : 0;
      return wa !== wb ? wa - wb : a.start_time.localeCompare(b.start_time);
    })
    .map((s) => monthlyPhrase(s));

  return [...weekly, ...everyTwoWeeks, ...monthly];
}

/** "Monthly on the second Tuesday at 10:00 AM", naming the session's own
 *  therapist after the time when it has one. */
function monthlyPhrase(s: SpiceScheduleSession): string {
  const label = s.week_of_month ? SPICE_WEEK_OF_MONTH_LABELS[s.week_of_month].toLowerCase() : '';
  return `Monthly on the ${label} ${SPICE_DAY_NAMES[s.weekday] ?? ''} at ${formatSpiceTime(s.start_time)}${withTherapist(sessionTherapistName(s))}`;
}

/** The schedule's lines: one per kind that has sessions, SPICE first. A
 *  plan with no sessions has no lines. */
export function formatSpiceScheduleLines(sessions: readonly SpiceScheduleSession[]): SpiceScheduleLine[] {
  const lines: SpiceScheduleLine[] = [];
  for (const kind of KIND_ORDER) {
    const ofKind = sessions.filter((s) => s.kind === kind);
    if (ofKind.length === 0) continue;
    lines.push({ kind: SPICE_KIND_LABELS[kind], text: kindPhrases(ofKind).join(', ') });
  }
  return lines;
}

/** The schedule as one plain line, for a surface with room for one (the
 *  week grid's client picker): the lines joined with "; ", each after its
 *  kind label, the label dropped when the only line is SPICE. */
export function formatSpiceScheduleText(sessions: readonly SpiceScheduleSession[]): string {
  const lines = formatSpiceScheduleLines(sessions);
  if (lines.length === 0) return SPICE_NO_SCHEDULE_TEXT;
  if (lines.length === 1 && lines[0]!.kind === 'SPICE') return lines[0]!.text;
  return lines.map((l) => `${l.kind}: ${l.text}`).join('; ');
}

/**
 * An away plan in words:
 *   - covered: "Covered by Amara Lindenbrook until 14 Oct", or "Covered
 *     by Amara Lindenbrook" with no until date;
 *   - paused: "Paused until 14 Oct (Vacation)", or "Paused (Vacation)"
 *     with no until date. The word in brackets is the note for Other with
 *     a note, else Vacation, Medical or Other. A Vacation or Medical note
 *     is shown apart, never folded in here.
 */
export function formatSpiceAway(away: {
  state: 'covered' | 'on_hold';
  hold_reason: SpiceHoldReason | null;
  hold_note: string | null;
  status_until: string | null;
  cover_name: string | null;
}): string {
  const until = away.status_until ? formatSpiceShortDate(away.status_until) : null;
  if (away.state === 'covered') {
    const who = away.cover_name ?? '';
    return until ? `Covered by ${who} until ${until}` : `Covered by ${who}`;
  }
  const reason = away.hold_reason ?? 'other';
  const note = away.hold_note?.trim() ?? '';
  const word = reason === 'other' && note !== '' ? note : HOLD_REASON_WORDS[reason];
  return until ? `Paused until ${until} (${word})` : `Paused (${word})`;
}

/**
 * The away phrase for a wire that carries the plan's one status word
 * beside its reason (My week's Not this week line, Your day's row). The
 * server has already read the state against the practice day: covered
 * reads covered from the wire's own reason, date and cover name, and a
 * pause's word (on_hold, vacation, hospital) reads paused, its reason
 * mapped through effectiveHold with no date cutoff of its own.
 */
export function formatSpiceAwayOfStatus(
  away: {
    status: SpiceVisitPlanStatus;
    hold_reason: SpiceHoldReason | null;
    hold_note: string | null;
    status_until: string | null;
    cover_name: string | null;
  },
  todayIso: string,
): string {
  if (away.status === 'covered') {
    return formatSpiceAway({
      state: 'covered',
      hold_reason: away.hold_reason,
      hold_note: away.hold_note,
      status_until: away.status_until,
      cover_name: away.cover_name,
    });
  }
  return formatSpiceAway({
    state: 'on_hold',
    hold_reason: effectiveHold(away.status, away.hold_reason, null, { coverRosterId: null }, todayIso).hold_reason,
    hold_note: away.hold_note,
    status_until: away.status_until,
    cover_name: null,
  });
}

/** A session from the record aggregate's camelCase shape. */
export function sessionFromVitals(s: {
  weekday: number;
  startTime: string;
  kind: SpiceSessionKind;
  frequency: SpiceScheduleSession['frequency'];
  anchorWeek: string | null;
  weekOfMonth: SpiceWeekOfMonth | null;
  durationMinutes: number | null;
  therapistRosterId?: string;
  therapistName?: string;
}): SpiceScheduleSession {
  return {
    weekday: s.weekday,
    start_time: s.startTime,
    kind: s.kind,
    frequency: s.frequency,
    anchor_week: s.anchorWeek,
    week_of_month: s.weekOfMonth,
    duration_minutes: s.durationMinutes,
    ...(s.therapistRosterId === undefined ? {} : { therapist_roster_id: s.therapistRosterId }),
    ...(s.therapistName === undefined ? {} : { therapist_name: s.therapistName }),
  };
}
